import { FC, CSSProperties, useState, useMemo } from 'react';
import { 
  AutoComplete, 
  Input, 
  Button, 
  Segmented, 
  Empty, 
  Typography,
  theme,
} from 'antd';
import { SearchOutlined, CloseCircleOutlined, EnterOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSearch, CollectionFilter } from '../../use/useSearch';
import { normalizeSearchResult } from '../../services/searchConfig';
import { SearchResultCard } from './SearchResultCard';
import FlexBox from './FlexBox';

/**
 * Props for search components
 */
interface SearchProps {
  placeholder?: string;
  autoCompleteStyle?: CSSProperties;
  inputStyle?: CSSProperties;
  onSelect?: (value: string) => void;
  value?: string;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
}

const COLLECTION_FILTER_OPTIONS = [
  { label: 'All', value: 'all' },
  { label: 'Forms', value: 'formVersions' },
  { label: 'Records', value: 'records' },
];

const EMPTY_RESULT_KEY = '__no_results__';

const EmptySearchResult: FC = () => (
  <FlexBox column style={{ padding: '12px 16px' }}>
    <Empty 
      image={Empty.PRESENTED_IMAGE_SIMPLE} 
      description={
        <Typography.Text type="secondary">
          No results found
        </Typography.Text>
      }
      styles={{ image: { height: 40 } }}
    />
  </FlexBox>
);

/**
 * Renders a search component with autocomplete functionality.
 * @param {SearchProps} props - The props for the Search component.
 * @returns {JSX.Element} An AutoComplete component with search functionality.
 */
const Search: FC<SearchProps> = ({
  placeholder = 'Search...',
  autoCompleteStyle,
  inputStyle,
  onSelect,
}) => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const [collectionFilter, setCollectionFilter] = useState<CollectionFilter>('all');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { searchQuery, setSearchQuery, searchResults, refreshSearch } = useSearch({
    collectionFilter
  });

  const hasSearchQuery = searchQuery.trim().length > 0;
  const hasResults = searchResults.length > 0;

  const options = useMemo(() => {
    if (hasResults) {
      return searchResults.map(rawItem => {
        const normalizedItem = normalizeSearchResult(rawItem);
        return {
          value: normalizedItem.id,
          label: <SearchResultCard {...normalizedItem} />
        };
      });
    }
    
    if (hasSearchQuery) {
      return [{
        value: EMPTY_RESULT_KEY,
        label: <EmptySearchResult />,
        disabled: true
      }];
    }
    
    return [];
  }, [searchResults, hasSearchQuery, hasResults]);

  const handleAdvancedSearch = () => {
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.set('q', searchQuery.trim());
    if (collectionFilter !== 'all') params.set('type', collectionFilter);
    navigate(`/search?${params.toString()}`);
    setSearchQuery('');
  };

  const handleSelect = (value: string) => {
    if (value === EMPTY_RESULT_KEY) return;
    onSelect?.(value);
    setSearchQuery('');
  };

  const handleOpenChange = (visible: boolean) => {
    setIsDropdownOpen(visible);
  };

  return (
    <AutoComplete
      options={options}
      open={isDropdownOpen && hasSearchQuery}
      onOpenChange={handleOpenChange}
      onSelect={handleSelect}
      popupMatchSelectWidth={false}
      style={{
        minWidth: 120,
        maxWidth: 500,
        width: '100%',
        ...autoCompleteStyle
      }}
      styles={{
        popup: {
          root: {
            maxWidth: 200
          }
        }
      }}
      value={searchQuery}
      popupRender={(menu) => (
        <div>
          <FlexBox style={{ padding: '4px 8px' }}>
            <Segmented
              size="small"
              value={collectionFilter}
              onChange={(value) => setCollectionFilter(value as CollectionFilter)}
              options={COLLECTION_FILTER_OPTIONS}
              aria-label="Filter by collection type"
            />
          </FlexBox>
          {menu}
          <FlexBox justifyEnd noGrow style={{ padding: `${token.paddingXXS}px ${token.paddingXS}px` }}>
            <Button type='dashed' size='small' onClick={handleAdvancedSearch} aria-label="Advanced search">
              Advanced Search<EnterOutlined />
            </Button>
          </FlexBox>
        </div>
      )}
    >
      <Input
        autoFocus={false}
        variant='filled'
        style={{
          width: '100%',
          ...inputStyle
        }}
        placeholder={placeholder}
        value={searchQuery}
        prefix={<SearchOutlined />}
        onChange={(e) => setSearchQuery(e.target.value)}
        onFocus={refreshSearch}
        onPressEnter={handleAdvancedSearch}
        allowClear={{ clearIcon: <CloseCircleOutlined /> }}
      />
    </AutoComplete>
  );
};

export default Search;
